import { getDepartmentTree as getMeetingRoomDepartmentTree } from '../api/meetingRoomApi';
import { normalizeDeptId, deptIdsEqual } from './deptCascaderProps';

export function getDeptNodeId(node) {
  if (!node) return '';
  const raw = node.value != null ? node.value : node.Id;
  return normalizeDeptId(raw);
}

export function getDeptNodeLabel(node) {
  if (!node) return '';
  return node.label || node.Name || node.title || '';
}

export function getDeptOrgType(node) {
  if (!node) return '';
  return String(node.DeptOrgType || node.deptOrgType || '').toLowerCase();
}

export function isClassNode(node) {
  return getDeptOrgType(node) === 'class';
}

/**
 * 从完整部门树中定位当前学校节点。最后修改：2026-10-18
 */
export function findSchoolNodeInTree(nodes, schoolId) {
  const list = Array.isArray(nodes) ? nodes : (nodes ? [nodes] : []);
  for (const node of list) {
    const nodeId = getDeptNodeId(node);
    if (nodeId && deptIdsEqual(nodeId, schoolId)) return node;
    if (node.children && node.children.length) {
      const found = findSchoolNodeInTree(node.children, schoolId);
      if (found) return found;
    }
  }
  return null;
}

/**
 * 根据部门 Id 查找名称
 */
export function findDeptLabelInTree(nodes, id, visited = new Set()) {
  const targetId = normalizeDeptId(id);
  if (!targetId) return '';
  for (const node of nodes || []) {
    const nodeId = getDeptNodeId(node);
    const sid = nodeId || '';
    if (sid === targetId) return getDeptNodeLabel(node);
    if (visited.has(sid)) continue;
    if (nodeId) visited.add(sid);
    const childLabel = findDeptLabelInTree(node.children, id, visited);
    if (childLabel) return childLabel;
  }
  return '';
}

function buildClassTreeFromNode(node) {
  if (!node) return null;
  if (isClassNode(node)) {
    return {
      value: getDeptNodeId(node),
      label: getDeptNodeLabel(node),
      DeptOrgType: 'class'
    };
  }
  const children = (node.children || [])
    .map(child => buildClassTreeFromNode(child))
    .filter(Boolean);
  if (!children.length) return null;
  const value = getDeptNodeId(node);
  if (!value) return null;
  return {
    value,
    label: getDeptNodeLabel(node),
    DeptOrgType: getDeptOrgType(node),
    children
  };
}

/**
 * 班级选择：学校 -> 校区 -> 年级 -> 行政班
 */
export function buildClassTreeOptions(schoolNode) {
  if (!schoolNode) return [];
  const built = buildClassTreeFromNode(schoolNode);
  return built ? [built] : [];
}

function mapOrgTreeNode(node) {
  if (!node) return null;
  const value = getDeptNodeId(node);
  if (!value) return null;
  const children = (node.children || [])
    .map(child => mapOrgTreeNode(child))
    .filter(Boolean);
  const item = {
    value,
    label: getDeptNodeLabel(node),
    DeptOrgType: getDeptOrgType(node)
  };
  if (children.length) item.children = children;
  return item;
}

/**
 * 组织多选：学校下全部节点（校区/年级/行政班等均可选）
 */
export function buildOrgTreeOptions(schoolNode) {
  if (!schoolNode) return [];
  const built = mapOrgTreeNode(schoolNode);
  return built ? [built] : [];
}

/**
 * 参会组织选择：跳过学校根节点，直接从校区/部门层展示
 */
export function buildOrgPickerTreeOptions(schoolNode) {
  if (!schoolNode) return [];
  return (schoolNode.children || [])
    .map(child => mapOrgTreeNode(child))
    .filter(Boolean);
}

/**
 * 按会议室校区裁剪学生/部门选择树；未绑校区时沿用全校范围。最后修改：2026-10-18
 */
export function buildCampusScopedPickerTrees(schoolNode, campusId) {
  if (!schoolNode) {
    return { orgTreeOptions: [], classTreeOptions: [] };
  }
  const campus = normalizeDeptId(campusId);
  if (!campus) {
    return {
      orgTreeOptions: buildOrgPickerTreeOptions(schoolNode),
      classTreeOptions: buildClassTreeOptions(schoolNode)
    };
  }
  const campusNode = findDeptNodeInTree([schoolNode], campus);
  if (!campusNode) {
    return {
      orgTreeOptions: buildOrgPickerTreeOptions(schoolNode),
      classTreeOptions: buildClassTreeOptions(schoolNode)
    };
  }
  const orgRoot = mapOrgTreeNode(campusNode);
  const classRoot = buildClassTreeFromNode(campusNode);
  return {
    orgTreeOptions: orgRoot ? [orgRoot] : [],
    classTreeOptions: classRoot ? [classRoot] : []
  };
}

/**
 * 收集节点全部下级 Id
 */
export function collectDescendantNodeIds(node) {
  const ids = [];
  function walk(item) {
    (item.children || []).forEach(child => {
      if (child.value) ids.push(child.value);
      walk(child);
    });
  }
  walk(node);
  return ids;
}

/**
 * 查找某节点在树中的全部上级 Id。最后修改：2026-10-18
 */
export function findAncestorNodeIds(nodes, targetId) {
  const ancestors = [];
  function dfs(list, stack) {
    for (const node of list || []) {
      const nextStack = stack.concat(node);
      if (deptIdsEqual(node.value, targetId)) {
        ancestors.push(...stack.map(item => item.value));
        return true;
      }
      if (node.children && dfs(node.children, nextStack)) return true;
    }
    return false;
  }
  dfs(nodes, []);
  return ancestors;
}

function findNodeInSubtree(node, targetId) {
  if (!node) return null;
  if (deptIdsEqual(node.value, targetId)) return node;
  for (const child of node.children || []) {
    const found = findNodeInSubtree(child, targetId);
    if (found) return found;
  }
  return null;
}

/**
 * 去掉已被上级节点覆盖的下级选择。最后修改：2026-10-18
 */
export function dedupeOrgSelectionNodes(checkedNodes, treeRoots) {
  return checkedNodes.filter(node => {
    return !checkedNodes.some(other => {
      if (deptIdsEqual(other.value, node.value)) return false;
      const otherNode = findDeptNodeInTree(treeRoots, other.value);
      if (!otherNode) return false;
      return !!findNodeInSubtree(otherNode, node.value);
    });
  });
}

/**
 * 部门节点参会：统一按部门(Type=3)通知，覆盖该节点及下属任教教师
 */
export function resolveOrgParticipantType() {
  return 3;
}

/**
 * 拉取当前学校部门树根节点。最后修改：2026-10-18
 */
export function loadSchoolDeptTree(schoolId) {
  if (!normalizeDeptId(schoolId)) return Promise.resolve(null);
  return getMeetingRoomDepartmentTree({ pid: 0 }).then(res => {
    if (!res.data || !res.data.success) return null;
    return findSchoolNodeInTree(res.data.response, schoolId);
  });
}

/**
 * 在树中查找节点。最后修改：2026-10-18
 */
export function findDeptNodeInTree(nodes, id, visited = new Set()) {
  const targetId = normalizeDeptId(id);
  if (!targetId) return null;
  for (const node of nodes || []) {
    const nodeId = getDeptNodeId(node);
    const sid = nodeId || '';
    if (sid === targetId) return node;
    if (visited.has(sid)) continue;
    if (nodeId) visited.add(sid);
    const found = findDeptNodeInTree(node.children, id, visited);
    if (found) return found;
  }
  return null;
}
